import { useEffect, useMemo, useState } from "react";
import { showGlobalToast, showGlobalConfirm } from "../utils/globalNotify";
import { Card, Input, Textarea, Btn } from "../components/ui";
import { authFetch, apiUrl, slugify } from "../utils/api";
import { ROLE_KEY, BIZ_SELECT_KEY } from "../utils/constants";
import { GlobalDocTemplatesModal, BusinessDocumentsModal } from "./SysadminDocs";
import { EmailTemplatesModal, SendEmailModal, BulkSendEmailModal } from "./SysadminEmail";

type BusinessRec = {
  id: number;
  name: string;
  slug: string;
  status?: "active" | "blocked" | "archived";
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  md_checklist_type?: "ambulant" | "aki";
  trial?: boolean;
  trial_ends_at?: string | null;
  trial_active?: boolean;
  trial_expired?: boolean;
  trial_days_left?: number | null;
  created_at?: string;
  // Systemadmin-Übersicht (additiv aus der Listen-API):
  ik?: string | null;
  street?: string | null;
  zip_city?: string | null;
  iso_9001_enabled?: boolean;
  iso_15224_enabled?: boolean;
  template_kind?: string | null;
  member_count?: number;
  qm_chapters_total?: number;
  qm_chapters_filled?: number;
  logo_present?: boolean;
  logo_url?: string | null;
  price_monthly?: number;
  setup_fee?: number;
};

const DEFAULT_PRICE_MONTHLY = 170;
const DEFAULT_SETUP_FEE = 400;

type FilterKey = "all" | "active" | "trial" | "blocked" | "archived" | "templates";

// ---- Line-Icons (Stroke-Stil, currentColor) --------------------------------
const Ico = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5M12 15V3" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>,
  up: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>,
  open: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="M10 17l5-5-5-5M15 12H3" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M4 20h4L20 8a2.8 2.8 0 0 0-4-4L4 16v4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
  unlock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></svg>,
  archive: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></svg>,
  login: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="M10 17l5-5-5-5M15 12H3" /></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>,
  docs: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 13h6M9 17h6" /></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>,
};

// Standard-Enddatum: heute + 14 Tage (YYYY-MM-DD).
function defaultTrialEnd(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "–";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// Farbe der Initialen-Kachel: Vorlage = orange, AKI = violett, sonst teal.
function tileStyle(b: BusinessRec): { background: string; color: string } {
  if (b.template_kind) return { background: "var(--orange-light)", color: "var(--orange-dark)" };
  if (b.md_checklist_type === "aki") return { background: "#EDE9FB", color: "#4A3A8C" };
  return { background: "var(--teal-light)", color: "var(--teal-deep)" };
}

// Balkenfarbe nach Füllgrad.
function barColor(pct: number): string {
  if (pct >= 90) return "var(--green)";
  if (pct >= 60) return "var(--teal)";
  if (pct >= 30) return "var(--orange)";
  return "var(--red)";
}

function shortDate(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

export function SystemAdminPanel() {
  const [businesses, setBusinesses] = useState<BusinessRec[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bizDraft, setBizDraft] = useState<Partial<BusinessRec>>({ name: "", slug: "", md_checklist_type: "ambulant", price_monthly: DEFAULT_PRICE_MONTHLY, setup_fee: DEFAULT_SETUP_FEE });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [adminDraft, setAdminDraft] = useState<{ email: string; firstName: string; lastName: string }>({ email: "", firstName: "", lastName: "" });
  const [docTemplatesOpen, setDocTemplatesOpen] = useState(false);
  const [docsForBusiness, setDocsForBusiness] = useState<BusinessRec | null>(null);
  const [emailTemplatesOpen, setEmailTemplatesOpen] = useState(false);
  const [emailForBusiness, setEmailForBusiness] = useState<BusinessRec | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = apiUrl("/api/v1/businesses");
      const resp = await authFetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setBusinesses(data);
    } catch (e: unknown) {
      setError("Organisationen konnten nicht geladen werden.");
      console.error("Business load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ---- Handler (Logik unverändert) ----------------------------------------
  const submitBiz = async () => {
    if (!bizDraft.name) { showGlobalToast("Name angeben.", "warning"); return; }
    if (!editingId) {
      const emailOk = /.+@.+\..+/.test(adminDraft.email.trim());
      if (!adminDraft.email.trim() || !adminDraft.firstName.trim() || !adminDraft.lastName.trim()) {
        showGlobalToast("Bitte Admin-Daten (E-Mail, Vor- und Nachname) angeben.", "warning");
        return;
      }
      if (!emailOk) { showGlobalToast("Bitte eine gültige Admin-E-Mail angeben.", "warning"); return; }
    }
    const business: Record<string, unknown> = {
      name: bizDraft.name,
      contact_email: bizDraft.contact_email,
      contact_phone: bizDraft.contact_phone,
      street: bizDraft.street,
      zip_city: bizDraft.zip_city,
      address: bizDraft.address,
      md_checklist_type: bizDraft.md_checklist_type || "ambulant",
      trial: !!bizDraft.trial,
      trial_ends_at: bizDraft.trial ? (bizDraft.trial_ends_at || defaultTrialEnd()) : null,
      price_monthly: bizDraft.price_monthly ?? DEFAULT_PRICE_MONTHLY,
      setup_fee: bizDraft.setup_fee ?? DEFAULT_SETUP_FEE,
    };
    if (!editingId) business.slug = slugify(bizDraft.name || "");
    try {
      const resp = await authFetch(
        apiUrl(editingId ? `/api/v1/businesses/${editingId}` : "/api/v1/businesses"),
        {
          method: editingId ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ business }),
        },
      );
      if (!resp.ok) {
        const msg = await resp.json().catch(() => ({}));
        throw new Error((msg.errors || []).join(", ") || `HTTP ${resp.status}`);
      }
      if (!editingId) {
        const created = await resp.json().catch(() => ({}));
        const newBizId = created?.id;
        if (!newBizId) throw new Error("Organisation angelegt, aber ID fehlt.");
        const adminResp = await authFetch(apiUrl(`/api/v1/businesses/${newBizId}/users`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user: {
              email: adminDraft.email.trim(),
              first_name: adminDraft.firstName.trim(),
              last_name: adminDraft.lastName.trim(),
              password: crypto.randomUUID(),
              role: "business_admin",
              membership_role: "business_admin",
            },
          }),
        });
        if (!adminResp.ok) {
          const msg = await adminResp.json().catch(() => ({}));
          showGlobalToast("Organisation angelegt, aber Admin-Anlage fehlgeschlagen: " + ((msg.errors || []).join(", ") || `HTTP ${adminResp.status}`), "error");
          resetBizForm();
          await load();
          return;
        }
        showGlobalToast("Organisation und Admin angelegt. Zugangsdaten wurden per E-Mail versendet.", "success");
      } else {
        showGlobalToast("Organisation gespeichert.", "success");
      }
      resetBizForm();
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      showGlobalToast((editingId ? "Speichern fehlgeschlagen. " : "Anlegen fehlgeschlagen. ") + msg, "error");
      console.error(e);
    }
  };

  const resetBizForm = () => {
    setBizDraft({ name: "", slug: "", contact_email: "", contact_phone: "", address: "", md_checklist_type: "ambulant", price_monthly: DEFAULT_PRICE_MONTHLY, setup_fee: DEFAULT_SETUP_FEE });
    setAdminDraft({ email: "", firstName: "", lastName: "" });
    setEditingId(null);
    setShowCreate(false);
  };

  const startEdit = (b: BusinessRec) => {
    setBizDraft({
      name: b.name,
      contact_email: b.contact_email,
      contact_phone: b.contact_phone,
      street: b.street,
      zip_city: b.zip_city,
      address: b.address,
      md_checklist_type: b.md_checklist_type || "ambulant",
      trial: !!b.trial,
      trial_ends_at: b.trial_ends_at || undefined,
      price_monthly: b.price_monthly ?? DEFAULT_PRICE_MONTHLY,
      setup_fee: b.setup_fee ?? DEFAULT_SETUP_FEE,
    });
    setEditingId(b.id);
    setShowCreate(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteBusiness = async (b: BusinessRec) => {
    const ok = await showGlobalConfirm(
      "Organisation endgültig löschen",
      `Die Organisation "${b.name}" und ALLE zugehörigen Daten (Mitarbeitende, QM-Handbuch, Verfahrensanweisungen, Audits, Dokumente ...) werden unwiderruflich gelöscht. Dies kann NICHT rückgängig gemacht werden. Wirklich löschen?`,
    );
    if (!ok) return;
    try {
      const resp = await authFetch(apiUrl(`/api/v1/businesses/${b.id}`), { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.json().catch(() => ({}));
        throw new Error((msg.errors || []).join(", ") || `HTTP ${resp.status}`);
      }
      showGlobalToast("Organisation gelöscht.", "success");
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      showGlobalToast("Löschen fehlgeschlagen. " + msg, "error");
      console.error(e);
    }
  };

  const updateStatus = async (b: BusinessRec, status: "active" | "blocked" | "archived") => {
    const label = status === "active" ? "aktivieren" : status === "blocked" ? "sperren" : "archivieren";
    const ok = await showGlobalConfirm("Status ändern", `Organisation "${b.name}" wirklich ${label}?`);
    if (!ok) return;
    try {
      const resp = await authFetch(apiUrl(`/api/v1/businesses/${b.id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business: { status } }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await load();
    } catch (e) {
      console.error(e);
      showGlobalToast("Status konnte nicht aktualisiert werden.", "error");
    }
  };

  const loginAsBusiness = (b: BusinessRec) => {
    // System-Admin betritt den Mandanten: echte Identitaet bleibt erhalten
    // (kein Pseudo-Nutzer). Nur den aktiven Mandanten setzen; App.tsx zeigt
    // dann die Mandanten-Ansicht UND behaelt die System-Admin-Bereiche.
    localStorage.setItem(BIZ_SELECT_KEY, String(b.id));
    localStorage.setItem(ROLE_KEY, "admin");
    window.location.reload();
  };

  // ---- Ableitungen (alle Zähler aus derselben Liste) ----------------------
  const real = useMemo(() => businesses.filter((b) => !b.template_kind), [businesses]);
  const templates = useMemo(() => businesses.filter((b) => !!b.template_kind), [businesses]);

  const counts = useMemo(() => ({
    all: businesses.length,
    active: real.filter((b) => (b.status || "active") === "active").length,
    trial: real.filter((b) => !!b.trial).length,
    blocked: real.filter((b) => b.status === "blocked").length,
    archived: real.filter((b) => b.status === "archived").length,
    templates: templates.length,
  }), [businesses, real, templates]);

  const kpis = useMemo(() => {
    const userTotal = real.reduce((sum, b) => sum + (b.member_count || 0), 0);
    const avgUsers = real.length ? Math.round(userTotal / real.length) : 0;
    const trialsOpen = real.filter((b) => b.trial && !b.trial_expired).length;
    const trialsExpired = real.filter((b) => b.trial_expired).length;
    const endingSoon = real.filter((b) => b.trial_active && (b.trial_days_left ?? 99) <= 3).length;
    const now = new Date();
    const createdThisMonth = real.filter((b) => {
      if (!b.created_at) return false;
      const d = new Date(b.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    return { activeCount: counts.active, userTotal, avgUsers, trialsOpen, trialsExpired, endingSoon, createdThisMonth };
  }, [real, counts.active]);

  const q = search.trim().toLowerCase();
  const matchesSearch = (b: BusinessRec) =>
    !q || [b.name, b.slug, b.ik, b.zip_city, b.contact_email].filter(Boolean).join(" ").toLowerCase().includes(q);
  const inFilter = (b: BusinessRec) => {
    switch (filter) {
      case "active": return !b.template_kind && (b.status || "active") === "active";
      case "trial": return !b.template_kind && !!b.trial;
      case "blocked": return !b.template_kind && b.status === "blocked";
      case "archived": return !b.template_kind && b.status === "archived";
      case "templates": return !!b.template_kind;
      default: return true;
    }
  };
  const visible = useMemo(() =>
    businesses
      .filter((b) => matchesSearch(b) && inFilter(b))
      .sort((a, b) => (Number(!!a.template_kind) - Number(!!b.template_kind)) || (a.name || "").localeCompare(b.name || "", "de", { sensitivity: "base" })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businesses, q, filter],
  );
  const visibleTemplates = visible.filter((b) => b.template_kind).length;

  // Mehrfachauswahl (nur echte Unternehmen; Vorlagen-Mandanten haben keine Kontakt-E-Mail).
  const selectableVisible = visible.filter((b) => !b.template_kind);
  const allSelected = selectableVisible.length > 0 && selectableVisible.every((b) => selectedIds.has(b.id));
  const toggleSelected = (id: number) => setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleSelectAll = () => setSelectedIds(() => (allSelected ? new Set() : new Set(selectableVisible.map((b) => b.id))));
  const clearSelection = () => setSelectedIds(new Set());

  // Handlungsbedarf (dynamisch, gekappt).
  const actions = useMemo(() => {
    const rows: { tone: "t" | "g" | "o" | "b"; icon: keyof typeof Ico; title: string; sub: string }[] = [];
    real.forEach((b) => {
      const filled = b.qm_chapters_filled ?? 0;
      const total = b.qm_chapters_total ?? 0;
      if (b.trial_active && (b.trial_days_left ?? 99) <= 3) {
        rows.push({ tone: "o", icon: "clock", title: `${b.name} – Testzugang endet in ${b.trial_days_left} Tag${b.trial_days_left === 1 ? "" : "en"}`, sub: `Erst ${filled} von ${total} QM-Kapiteln gefüllt. Rückruf empfohlen oder Testzugang verlängern.` });
      } else if (b.trial_expired) {
        rows.push({ tone: "o", icon: "warn", title: `${b.name} – Testzugang abgelaufen`, sub: `Funktionen eingeschränkt. Testzeitraum verlängern oder Trial-Flag entfernen.` });
      }
    });
    real.forEach((b) => {
      if ((b.status || "active") === "active" && !b.logo_present) {
        rows.push({ tone: "b", icon: "image", title: `${b.name} – Logo fehlt`, sub: `Ohne hinterlegtes Logo erscheinen PDF-Exporte ohne Briefkopf.` });
      }
    });
    return rows.slice(0, 6);
  }, [real]);

  // Verteilung.
  const dist = useMemo(() => {
    const n = real.length || 1;
    const amb = real.filter((b) => (b.md_checklist_type || "ambulant") === "ambulant").length;
    const aki = real.filter((b) => b.md_checklist_type === "aki").length;
    const iso9 = real.filter((b) => b.iso_9001_enabled).length;
    const iso15 = real.filter((b) => b.iso_15224_enabled).length;
    const logo = real.filter((b) => b.logo_present).length;
    const totalCh = real.reduce((s, b) => s + (b.qm_chapters_total || 0), 0);
    const filledCh = real.reduce((s, b) => s + (b.qm_chapters_filled || 0), 0);
    const avgFilled = real.length ? filledCh / real.length : 0;
    const avgTotal = real.length ? totalCh / real.length : 0;
    return {
      amb, aki, iso9, iso15, logo, n: real.length,
      isoPct9: Math.round((iso9 / n) * 100),
      isoPct15: Math.round((iso15 / n) * 100),
      logoPct: Math.round((logo / n) * 100),
      mdAmbPct: Math.round((amb / n) * 100),
      mdAkiPct: Math.round((aki / n) * 100),
      avgFilled, avgTotal,
      qmPct: avgTotal ? Math.round((avgFilled / avgTotal) * 100) : 0,
    };
  }, [real]);

  const chip = (key: FilterKey, label: string, count: number) => (
    <button
      type="button"
      onClick={() => setFilter(key)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-[7px] text-[.78rem] font-semibold transition-colors ${
        filter === key
          ? "border-[var(--teal)] bg-[var(--teal)] text-white"
          : "border-[var(--line)] bg-white text-[var(--ink-mid)] hover:border-[var(--teal-mid)] hover:text-[var(--teal-dark)]"
      }`}
    >
      {label}<b className={`text-[.74rem] font-bold ${filter === key ? "opacity-85" : "opacity-60"}`}>{count}</b>
    </button>
  );

  const iconBtn = (icon: keyof typeof Ico, title: string, onClick: () => void, danger = false) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`grid h-[30px] w-[30px] place-items-center rounded-[7px] border border-transparent text-[var(--ink-muted)] transition-colors [&_svg]:h-[15px] [&_svg]:w-[15px] ${
        danger
          ? "hover:border-[#F5C6C6] hover:bg-[var(--red-light)] hover:text-[#5C1616]"
          : "hover:border-[var(--teal-mid)] hover:bg-[var(--teal-light)] hover:text-[var(--teal-deep)]"
      }`}
    >
      {Ico[icon]}
    </button>
  );

  const subline = `${counts.active + counts.blocked + counts.archived} Pflegedienste · ${counts.active} aktiv · ${counts.blocked} gesperrt · ${counts.archived} archiviert · ${counts.trial} mit Testzugang${counts.templates ? ` · zusätzlich ${counts.templates} Vorlagen-Mandanten` : ""}`;

  return (
    <div className="w-full max-w-[1560px] px-6 pb-10 pt-6">
      {/* Kopf */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-5">
        <div>
          <h1 className="text-[1.55rem] font-extrabold leading-[1.2] tracking-[-.025em] text-[var(--ink)]">Unternehmen</h1>
          <div className="mt-[5px] text-[.84rem] text-[var(--ink-muted)]">{subline}</div>
        </div>
        <div className="flex gap-2.5">
          <Btn variant="secondary" onClick={() => setDocTemplatesOpen(true)}>
            <span className="[&_svg]:h-[15px] [&_svg]:w-[15px]">{Ico.docs}</span> Dokument-Vorlagen
          </Btn>
          <Btn variant="secondary" onClick={() => setEmailTemplatesOpen(true)}>
            <span className="[&_svg]:h-[15px] [&_svg]:w-[15px]">{Ico.mail}</span> E-Mail-Vorlagen
          </Btn>
          <Btn variant="secondary" onClick={() => window.print()}>
            <span className="[&_svg]:h-[15px] [&_svg]:w-[15px]">{Ico.download}</span> Liste exportieren
          </Btn>
          <Btn onClick={() => (showCreate ? resetBizForm() : setShowCreate(true))}>
            <span className="[&_svg]:h-[15px] [&_svg]:w-[15px]">{Ico.plus}</span> {showCreate ? "Formular schließen" : "Unternehmen anlegen"}
          </Btn>
        </div>
      </div>

      {/* KPIs */}
      <div className="mb-[18px] grid gap-[13px]" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(148px,1fr))" }}>
        <KpiCard label="Aktive Unternehmen" value={kpis.activeCount} tone="teal" foot={kpis.createdThisMonth > 0 ? { up: true, text: `+${kpis.createdThisMonth} diesen Monat angelegt` } : { up: false, text: `${counts.all} gesamt` }} />
        <KpiCard label="Benutzerkonten" value={kpis.userTotal} tone="green" foot={{ up: false, text: `Ø ${kpis.avgUsers} pro Unternehmen` }} />
        <KpiCard label="Testzugänge offen" value={kpis.trialsOpen} tone="orange" foot={{ up: false, text: kpis.endingSoon > 0 ? `${kpis.endingSoon} endet bald` : "keine dringend" }} />
        <KpiCard label="Testzugang abgelaufen" value={kpis.trialsExpired} tone="red" foot={{ up: false, text: kpis.trialsExpired > 0 ? "Zugang eingeschränkt" : "alles ok" }} />
      </div>

      {/* Anlegen/Bearbeiten */}
      {showCreate && (
        <Card className="mb-4 grid gap-3 p-5">
          <div className="text-sm font-bold text-[var(--ink)]">{editingId ? "Organisation bearbeiten" : "Neue Organisation"}</div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">Name</label>
              <Input value={bizDraft.name || ""} onChange={(e) => setBizDraft((d) => ({ ...d, name: e.target.value, slug: slugify(e.target.value) }))} />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">Kontakt E-Mail</label>
              <Input value={bizDraft.contact_email || ""} onChange={(e) => setBizDraft((d) => ({ ...d, contact_email: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">Telefonnummer</label>
              <Input value={bizDraft.contact_phone || ""} onChange={(e) => setBizDraft((d) => ({ ...d, contact_phone: e.target.value }))} />
            </div>
            <div className="grid gap-2 md:col-span-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">Anschrift</label>
              <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-muted)]">Straße &amp; Hausnummer</label>
                  <Input value={bizDraft.street || ""} onChange={(e) => setBizDraft((d) => ({ ...d, street: e.target.value }))} />
                </div>
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-muted)]">PLZ / Ort</label>
                  <Input value={bizDraft.zip_city || ""} onChange={(e) => setBizDraft((d) => ({ ...d, zip_city: e.target.value }))} />
                </div>
              </div>
              <div className="text-xs text-[var(--ink-muted)]">Fließt in die generierten Dokumente (Vertrag/AVV/TOM) ein.</div>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">MD Checkliste</label>
              <select
                className="h-11 rounded-xl border border-[var(--line)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(0,159,181,.25)]"
                value={bizDraft.md_checklist_type || "ambulant"}
                onChange={(e) => setBizDraft((d) => ({ ...d, md_checklist_type: e.target.value as BusinessRec["md_checklist_type"] }))}
              >
                <option value="ambulant">Ambulante Pflege</option>
                <option value="aki">Außerklinische Intensivpflege</option>
              </select>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">Probezeitraum</label>
              <label className="inline-flex items-center gap-2 text-sm text-[var(--ink-mid)]">
                <input
                  type="checkbox"
                  className="accent-[var(--teal)]"
                  checked={!!bizDraft.trial}
                  onChange={(e) => setBizDraft((d) => ({ ...d, trial: e.target.checked, trial_ends_at: e.target.checked ? (d.trial_ends_at || defaultTrialEnd()) : null }))}
                />
                Kostenfreier Probezeitraum
              </label>
              {bizDraft.trial && (
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-muted)]">Endet am</label>
                  <Input type="date" value={bizDraft.trial_ends_at || defaultTrialEnd()} onChange={(e) => setBizDraft((d) => ({ ...d, trial_ends_at: e.target.value }))} />
                </div>
              )}
            </div>
            <div className="grid gap-2 md:col-span-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">Preis (netto)</label>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-muted)]">Monatspreis € / Monat</label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={bizDraft.price_monthly ?? DEFAULT_PRICE_MONTHLY}
                    onChange={(e) => setBizDraft((d) => ({ ...d, price_monthly: e.target.value === "" ? undefined : Number(e.target.value) }))}
                  />
                </div>
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-muted)]">Einrichtungsgebühr € (einmalig)</label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={bizDraft.setup_fee ?? DEFAULT_SETUP_FEE}
                    onChange={(e) => setBizDraft((d) => ({ ...d, setup_fee: e.target.value === "" ? undefined : Number(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="text-xs text-[var(--ink-muted)]">Standard: 170 € / Monat netto + 400 € Einrichtungsgebühr netto. Pro Unternehmen anpassbar.</div>
            </div>
            <div className="grid gap-2 md:col-span-2">
              <label className="text-sm font-medium text-[var(--ink-mid)]">Adresse/Notizen</label>
              <Textarea className="md:col-span-2" value={bizDraft.address || ""} onChange={(e) => setBizDraft((d) => ({ ...d, address: e.target.value }))} />
            </div>
          </div>
          {!editingId && (
            <div className="grid gap-2 rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3">
              <div className="text-sm font-bold text-[var(--ink)]">Administrator dieser Organisation</div>
              <div className="text-xs text-[var(--ink-muted)]">Die Zugangsdaten werden automatisch per E-Mail an den Admin versendet.</div>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-mid)]">Vorname</label>
                  <Input value={adminDraft.firstName} onChange={(e) => setAdminDraft((a) => ({ ...a, firstName: e.target.value }))} />
                </div>
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-mid)]">Nachname</label>
                  <Input value={adminDraft.lastName} onChange={(e) => setAdminDraft((a) => ({ ...a, lastName: e.target.value }))} />
                </div>
                <div className="grid gap-1">
                  <label className="text-xs text-[var(--ink-mid)]">E-Mail</label>
                  <Input type="email" value={adminDraft.email} onChange={(e) => setAdminDraft((a) => ({ ...a, email: e.target.value }))} />
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Btn onClick={submitBiz}>{editingId ? "Änderungen speichern" : "Speichern"}</Btn>
            <Btn variant="secondary" onClick={resetBizForm}>Abbrechen</Btn>
            {error && <span className="text-sm text-[var(--red)]">{error}</span>}
          </div>
        </Card>
      )}

      {/* Bulk-Auswahl-Leiste */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-10 mb-3 flex flex-wrap items-center gap-3 rounded-[12px] border border-[var(--teal-mid)] bg-[var(--teal-light)] px-4 py-2.5 shadow-[var(--elev)]">
          <div className="text-sm font-semibold text-[var(--teal-deep)]">{selectedIds.size} ausgewählt</div>
          <Btn className="py-[7px] text-xs" onClick={() => setBulkEmailOpen(true)}>
            <span className="[&_svg]:h-[14px] [&_svg]:w-[14px]">{Ico.mail}</span> E-Mail an Auswahl senden
          </Btn>
          <Btn variant="secondary" className="py-[7px] text-xs" onClick={clearSelection}>Auswahl aufheben</Btn>
        </div>
      )}

      {/* Tabelle */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-[var(--line-soft)] px-[18px] py-[14px]">
          <div className="relative min-w-[220px] max-w-[330px] flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] [&_svg]:h-[15px] [&_svg]:w-[15px]">{Ico.search}</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, Slug, IK-Nummer oder Ort…"
              className="w-full rounded-[9px] border border-[var(--line)] bg-[var(--surface)] py-[9px] pl-[35px] pr-3 text-[.83rem] text-[var(--ink)] outline-none transition-colors placeholder:text-[var(--ink-muted)] focus:border-[var(--teal)] focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,159,181,.1)]"
            />
          </div>
          <div className="flex flex-wrap gap-[7px]">
            {chip("all", "Alle", counts.all)}
            {chip("active", "Aktiv", counts.active)}
            {chip("trial", "Testzugang", counts.trial)}
            {chip("blocked", "Gesperrt", counts.blocked)}
            {chip("archived", "Archiviert", counts.archived)}
            {chip("templates", "Vorlagen", counts.templates)}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] border-collapse">
            <thead>
              <tr className="[&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-[var(--line)] [&_th]:bg-[var(--surface)] [&_th]:px-4 [&_th]:py-[11px] [&_th]:text-left [&_th]:text-[.67rem] [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-[.08em] [&_th]:text-[var(--ink-muted)]">
                <th className="w-10 pr-0"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="block h-[15px] w-[15px] accent-[var(--teal)]" aria-label="Alle auswählen" /></th>
                <th>Unternehmen</th>
                <th>IK-Nummer</th>
                <th>MD-Checkliste</th>
                <th>Status</th>
                <th>ISO-Module</th>
                <th>Benutzer</th>
                <th>QM-Handbuch</th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-[var(--ink-muted)]">Lade…</td></tr>
              )}
              {!loading && visible.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-[var(--ink-muted)]">Keine Organisationen für diese Auswahl.</td></tr>
              )}
              {!loading && visible.map((b) => {
                const status = b.status || "active";
                const total = b.qm_chapters_total ?? 0;
                const filled = b.qm_chapters_filled ?? 0;
                const pct = total ? Math.round((filled / total) * 100) : 0;
                const isTpl = !!b.template_kind;
                const t = tileStyle(b);
                return (
                  <tr key={b.id} className="[&_td]:border-b [&_td]:border-[var(--line-soft)] [&_td]:px-4 [&_td]:py-[13px] [&_td]:align-middle [&_td]:text-[.83rem] [&_td]:text-[var(--ink-mid)] hover:bg-[var(--surface)]">
                    <td className="w-10 pr-0"><input type="checkbox" disabled={isTpl} checked={selectedIds.has(b.id)} onChange={() => toggleSelected(b.id)} className="block h-[15px] w-[15px] accent-[var(--teal)] disabled:opacity-40" aria-label="Auswählen" /></td>
                    <td>
                      <div className="flex min-w-[236px] items-center gap-[11px]">
                        {b.logo_url ? (
                          <img src={b.logo_url} alt="" className="h-[34px] w-[34px] shrink-0 rounded-[9px] border border-[var(--line)] bg-white object-cover" />
                        ) : (
                          <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[9px] text-[.74rem] font-extrabold tracking-[-.02em]" style={t}>{initials(b.name)}</div>
                        )}
                        <div className="min-w-0">
                          <div className="text-[.86rem] font-bold leading-[1.35] text-[var(--ink)]">{b.name}</div>
                          <div className="font-mono text-[.74rem] text-[var(--ink-muted)]">{b.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {isTpl ? (
                        <div className="whitespace-nowrap tabular-nums text-[.81rem]">–<small className="mt-px block text-[.71rem] text-[var(--ink-muted)]">Vorlagen-Mandant</small></div>
                      ) : (
                        <div className="whitespace-nowrap tabular-nums text-[.81rem]">{b.ik || "—"}<small className="mt-px block text-[.71rem] text-[var(--ink-muted)]">{b.zip_city || "—"}</small></div>
                      )}
                    </td>
                    <td>
                      {b.md_checklist_type === "aki"
                        ? <span className="inline-block rounded-[5px] bg-[#EDE9FB] px-2 py-0.5 text-[.7rem] font-bold text-[#4A3A8C]">AKI</span>
                        : <span className="inline-block rounded-[5px] bg-[var(--teal-light)] px-2 py-0.5 text-[.7rem] font-bold text-[var(--teal-deep)]">ambulant</span>}
                    </td>
                    <td>{renderStatusCell(b, status, isTpl)}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        <IsoTag on={!!b.iso_9001_enabled}>9001</IsoTag>
                        <IsoTag on={!!b.iso_15224_enabled}>15224</IsoTag>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-[7px] whitespace-nowrap">
                        <span className="text-[var(--ink-muted)] [&_svg]:h-[14px] [&_svg]:w-[14px]">{Ico.users}</span>
                        <b className="font-bold text-[var(--ink)]">{b.member_count ?? 0}</b>
                      </div>
                    </td>
                    <td>
                      <div className="min-w-[128px]">
                        <div className="mb-[5px] flex justify-between text-[.72rem]">
                          <span>Kapitel gefüllt</span>
                          <b className="font-bold text-[var(--ink)] tabular-nums">{filled}/{total}</b>
                        </div>
                        <div className="h-[5px] overflow-hidden rounded-[4px] bg-[var(--line-soft)]">
                          <span className="block h-full rounded-[4px]" style={{ width: `${pct}%`, background: barColor(pct) }} />
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="flex justify-end gap-[5px]">
                        {iconBtn("open", isTpl ? "Vorlage öffnen" : "Öffnen (als Admin einloggen)", () => loginAsBusiness(b))}
                        {iconBtn("edit", "Stammdaten bearbeiten", () => startEdit(b))}
                        {!isTpl && iconBtn("docs", "Dokumente (Vertrag/AVV/TOM)", () => setDocsForBusiness(b))}
                        {!isTpl && iconBtn("mail", "E-Mail senden", () => setEmailForBusiness(b))}
                        {!isTpl && status === "active" && iconBtn("lock", "Sperren", () => updateStatus(b, "blocked"))}
                        {!isTpl && status === "blocked" && iconBtn("unlock", "Entsperren", () => updateStatus(b, "active"))}
                        {!isTpl && status !== "archived" && iconBtn("archive", "Archivieren", () => updateStatus(b, "archived"))}
                        {!isTpl && status === "archived" && iconBtn("refresh", "Reaktivieren", () => updateStatus(b, "active"))}
                        {!isTpl && iconBtn("trash", "Endgültig löschen", () => deleteBusiness(b), true)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line-soft)] px-[18px] py-[13px]">
          <div className="text-[.79rem] text-[var(--ink-muted)]">
            {visible.length} Treffer{visibleTemplates ? ` · ${visibleTemplates} Vorlagen-Mandant${visibleTemplates === 1 ? "" : "en"} eingeblendet` : ""}
          </div>
        </div>
      </Card>

      {/* Panels */}
      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_340px]">
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-[var(--line-soft)] px-[18px] py-[15px]">
            <h2 className="text-[.95rem] font-bold tracking-[-.01em] text-[var(--ink)]">Handlungsbedarf</h2>
            <span className="text-[.74rem] text-[var(--ink-muted)]">{actions.length} Hinweis{actions.length === 1 ? "" : "e"}</span>
          </div>
          <div>
            {actions.length === 0 ? (
              <div className="px-[18px] py-6 text-sm text-[var(--ink-muted)]">Keine offenen Hinweise.</div>
            ) : actions.map((a, i) => (
              <div key={i} className="flex items-start gap-[11px] border-b border-[var(--line-soft)] px-[18px] py-[13px] last:border-b-0">
                <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-[8px] [&_svg]:h-[14px] [&_svg]:w-[14px] ${aiTone(a.tone)}`}>{Ico[a.icon]}</div>
                <div>
                  <strong className="block text-[.81rem] font-semibold leading-[1.45] text-[var(--ink)]">{a.title}</strong>
                  <span className="mt-0.5 block text-[.73rem] text-[var(--ink-muted)]">{a.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="border-b border-[var(--line-soft)] px-[18px] py-[15px]">
            <h2 className="text-[.95rem] font-bold tracking-[-.01em] text-[var(--ink)]">Verteilung</h2>
          </div>
          <div className="flex flex-col gap-[14px] p-[18px]">
            <DistRow label="MD-Checkliste ambulant" value={String(dist.amb)} pct={dist.mdAmbPct} tone="teal" />
            <DistRow label="MD-Checkliste AKI" value={String(dist.aki)} pct={dist.mdAkiPct} tone="teal" />
            <DistRow label="ISO 9001 aktiviert" value={`${dist.iso9} von ${dist.n}`} pct={dist.isoPct9} tone="green" />
            <DistRow label="ISO 15224 aktiviert" value={`${dist.iso15} von ${dist.n}`} pct={dist.isoPct15} tone="green" />
            <DistRow label="Ø QM-Kapitel gefüllt" value={`${dist.avgFilled.toLocaleString("de-DE", { maximumFractionDigits: 1 })} von ${dist.avgTotal.toLocaleString("de-DE", { maximumFractionDigits: 0 })}`} pct={dist.qmPct} tone="teal" />
            <DistRow label="Logo für PDF-Export hinterlegt" value={`${dist.logo} von ${dist.n}`} pct={dist.logoPct} tone="green" />
          </div>
        </Card>
      </div>

      {docTemplatesOpen && <GlobalDocTemplatesModal onClose={() => setDocTemplatesOpen(false)} />}
      {docsForBusiness && (
        <BusinessDocumentsModal
          businessId={docsForBusiness.id}
          businessName={docsForBusiness.name}
          businessSlug={docsForBusiness.slug}
          onClose={() => setDocsForBusiness(null)}
        />
      )}
      {emailTemplatesOpen && <EmailTemplatesModal onClose={() => setEmailTemplatesOpen(false)} />}
      {emailForBusiness && (
        <SendEmailModal
          businessId={emailForBusiness.id}
          businessName={emailForBusiness.name}
          defaultTo={emailForBusiness.contact_email || ""}
          onClose={() => setEmailForBusiness(null)}
        />
      )}
      {bulkEmailOpen && (
        <BulkSendEmailModal
          businessIds={Array.from(selectedIds)}
          onClose={() => setBulkEmailOpen(false)}
          onSent={() => clearSelection()}
        />
      )}
    </div>
  );
}

// ---- kleine Unterkomponenten ----------------------------------------------
function KpiCard({ label, value, tone, foot }: { label: string; value: number; tone: "teal" | "green" | "orange" | "red"; foot: { up: boolean; text: string } }) {
  const bar = { teal: "var(--teal)", green: "var(--green)", orange: "var(--orange)", red: "var(--red)" }[tone];
  const valColor = { teal: "var(--ink)", green: "var(--green-dark)", orange: "var(--orange-dark)", red: "var(--red)" }[tone];
  return (
    <div className="relative overflow-hidden rounded-[12px] border border-[var(--line)] bg-white px-[18px] py-4 shadow-[var(--elev)]">
      <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: bar }} aria-hidden="true" />
      <div className="text-[.67rem] font-bold uppercase tracking-[.09em] text-[var(--ink-muted)]">{label}</div>
      <div className="my-[7px] mb-1 text-[1.85rem] font-extrabold leading-none tracking-[-.03em] tabular-nums" style={{ color: valColor }}>{value.toLocaleString("de-DE")}</div>
      <div className={`flex items-center gap-[5px] text-[.74rem] ${foot.up ? "text-[var(--green-dark)]" : "text-[var(--ink-muted)]"}`}>
        {foot.up && <span className="[&_svg]:h-3 [&_svg]:w-3">{Ico.up}</span>}{foot.text}
      </div>
    </div>
  );
}

function IsoTag({ on, children }: { on: boolean; children: React.ReactNode }) {
  return (
    <span className={`whitespace-nowrap rounded-[5px] px-[7px] py-0.5 text-[.69rem] font-bold ${on ? "bg-[var(--green-light)] text-[var(--green-dark)]" : "bg-[var(--line-soft)] text-[var(--ink-muted)]"}`}>{children}</span>
  );
}

function DistRow({ label, value, pct, tone }: { label: string; value: string; pct: number; tone: "teal" | "green" }) {
  return (
    <div>
      <div className="mb-[6px] flex items-baseline justify-between gap-2.5">
        <strong className="text-[.82rem] font-semibold text-[var(--ink)]">{label}</strong>
        <span className="whitespace-nowrap text-[.73rem] tabular-nums text-[var(--ink-muted)]">{value}</span>
      </div>
      <div className="h-[5px] overflow-hidden rounded-[4px] bg-[var(--line-soft)]">
        <span className="block h-full rounded-[4px]" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: tone === "teal" ? "var(--teal)" : "var(--green)" }} />
      </div>
    </div>
  );
}

function aiTone(tone: "t" | "g" | "o" | "b"): string {
  switch (tone) {
    case "g": return "bg-[var(--green-light)] text-[var(--green-dark)]";
    case "o": return "bg-[var(--orange-light)] text-[var(--orange-dark)]";
    case "b": return "bg-[var(--blue-light)] text-[#1B3E63]";
    default: return "bg-[var(--teal-light)] text-[var(--teal-deep)]";
  }
}

function renderStatusCell(b: BusinessRec, status: "active" | "blocked" | "archived", isTpl: boolean) {
  const pill = (cls: string, dot: string, label: string) => (
    <span className={`inline-flex items-center gap-[5px] whitespace-nowrap rounded-full px-[10px] py-[3px] text-[.71rem] font-bold ${cls}`}>
      <i className="block h-[6px] w-[6px] rounded-full" style={{ background: dot }} />{label}
    </span>
  );
  if (isTpl) {
    return pill("bg-[var(--orange-light)] text-[var(--orange-dark)]", "var(--orange)", `Vorlage · ${b.template_kind}`);
  }
  const statusPill =
    status === "blocked" ? pill("bg-[var(--red-light)] text-[#5C1616]", "var(--red)", "Gesperrt")
    : status === "archived" ? pill("bg-[var(--line-soft)] text-[var(--ink-mid)]", "var(--ink-muted)", "Archiviert")
    : pill("bg-[var(--green-light)] text-[var(--green-dark)]", "var(--green)", "Aktiv");
  let trialPill: React.ReactNode = null;
  if (b.trial) {
    if (b.trial_expired) {
      trialPill = pill("bg-[var(--orange-light)] text-[var(--orange-dark)]", "var(--orange)", `Test abgelaufen${b.trial_ends_at ? ` ${shortDate(b.trial_ends_at)}` : ""}`);
    } else {
      const days = b.trial_days_left ?? null;
      trialPill = pill("bg-[var(--blue-light)] text-[#1B3E63]", "var(--blue)", `Test bis ${shortDate(b.trial_ends_at)}${days != null ? ` · ${days} Tag${days === 1 ? "" : "e"}` : ""}`);
    }
  }
  if (!trialPill) return statusPill;
  return <div className="flex flex-col items-start gap-[5px]">{statusPill}{trialPill}</div>;
}
